import { Component, EventEmitter, Input, Output, OnInit, OnChanges, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltip } from '@angular/material/tooltip';
import { TooltipPipe } from '../custom-select/tooltip.pipe';
import { ClassNamePipe } from '../../pipes/class-name.pipe';
import { QueueService, QueueGroupsData } from '../../services/queue.service';

interface QueueGroup {
  name: string;
  queues: string[];
  isExpanded: boolean;
  isSelected: boolean;
}

interface QueueOption {
  name: string;
  isSelected: boolean;
  parentGroup: string;
}

@Component({
  selector: 'hierarchical-queue-selector',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatMenuModule,
    MatTooltip,
    TooltipPipe,
    ClassNamePipe,
  ],
  templateUrl: './hierarchical-queue-selector.component.html',
  styleUrl: './hierarchical-queue-selector.component.scss',
})
export class HierarchicalQueueSelectorComponent implements OnInit, OnChanges {
  @Input() selectedQueues: string[] = [];
  @Input() openAbove: boolean = false;
  @Input() dropDownWidth: string = '300px';
  @Output() onQueuesChange = new EventEmitter<string[]>();

  dropdownOpened: boolean = false;
  searchQuery: string = '';
  queueGroups: QueueGroup[] = [];
  allQueues: QueueOption[] = [];
  selectedOptionText: string = 'Select queue names';
  isOptionSelected: boolean = false;
  totalQueueCount: number = 0;

  constructor(
    private queueService: QueueService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.loadQueueGroups();
    this.updateSelectedOptionText();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedQueues'] && !changes['selectedQueues'].firstChange && this.queueGroups.length > 0) {
      this.syncSelectionFromInput();
    }
  }

  private syncSelectionFromInput(): void {
    this.queueGroups.forEach((group) => {
      group.isSelected = this.isGroupSelected(group.name, group.queues);
    });
    this.allQueues.forEach((queueOption) => {
      queueOption.isSelected = this.selectedQueues.includes(queueOption.name);
    });
    this.updateSelectedOptionText();
  }

  loadQueueGroups() {
    this.queueService.getQueueGroups().subscribe((data: QueueGroupsData) => {
      const queueGroupsData = data.data;

      this.queueGroups = Object.entries(queueGroupsData).map(([name, queues]) => ({
        name,
        queues,
        isExpanded: false,
        isSelected: this.isGroupSelected(name, queues)
      }));

      // Create flat list of all queues with parent group info
      this.allQueues = [];
      Object.entries(queueGroupsData).forEach(([groupName, queues]) => {
        queues.forEach(queue => {
          this.allQueues.push({
            name: queue,
            isSelected: this.selectedQueues.includes(queue),
            parentGroup: groupName
          });
        });
      });

      // Calculate total queue count and refresh display (e.g. when initial selection came from saved filter)
      this.totalQueueCount = this.allQueues.length;
      this.updateSelectedOptionText();
    });
  }

  isGroupSelected(groupName: string, queues: string[]): boolean {
    return queues.every(queue => this.selectedQueues.includes(queue));
  }

  handleButtonClick(event: Event): void {
    this.dropdownOpened = !this.dropdownOpened;
  }

  stopPropagation(event: Event): void {
    event.stopPropagation();
  }

  clearSearch(): void {
    this.searchQuery = '';
  }

  onMatMenuClosed() {}

  onOutsideClick(): void {
    this.dropdownOpened = false;
  }

  toggleGroupExpansion(group: QueueGroup, event: Event): void {
    event.stopPropagation();
    group.isExpanded = !group.isExpanded;
  }

  onGroupSelect(group: QueueGroup, event: Event): void {
    event.stopPropagation();

    if (group.isSelected) {
      // Deselect group - remove all queues in this group
      group.queues.forEach(queueName => {
        const index = this.selectedQueues.indexOf(queueName);
        if (index > -1) {
          this.selectedQueues.splice(index, 1);
        }
        // Update queue option
        const queueOption = this.allQueues.find(q => q.name === queueName);
        if (queueOption) {
          queueOption.isSelected = false;
        }
      });
      group.isSelected = false;
    } else {
      // Select group - add all queues in this group
      group.queues.forEach(queueName => {
        if (!this.selectedQueues.includes(queueName)) {
          this.selectedQueues.push(queueName);
        }
        // Update queue option
        const queueOption = this.allQueues.find(q => q.name === queueName);
        if (queueOption) {
          queueOption.isSelected = true;
        }
      });
      group.isSelected = true;
    }

    this.updateSelectedOptionText();
    this.onQueuesChange.emit([...this.selectedQueues]);
  }

  onGroupCheckboxClick(group: QueueGroup, event: Event): void {
    event.stopPropagation();
    // Prevent the row click event from firing when clicking the checkbox
  }

  onGroupCheckboxChange(group: QueueGroup, event: Event): void {
    event.stopPropagation();

    // Toggle the group selection
    group.isSelected = !group.isSelected;

    if (group.isSelected) {
      // Select all queues in this group
      group.queues.forEach(queueName => {
        if (!this.selectedQueues.includes(queueName)) {
          this.selectedQueues.push(queueName);
        }
        // Update queue option
        const queueOption = this.allQueues.find(q => q.name === queueName);
        if (queueOption) {
          queueOption.isSelected = true;
        }
      });
    } else {
      // Deselect all queues in this group
      group.queues.forEach(queueName => {
        const index = this.selectedQueues.indexOf(queueName);
        if (index > -1) {
          this.selectedQueues.splice(index, 1);
        }
        // Update queue option
        const queueOption = this.allQueues.find(q => q.name === queueName);
        if (queueOption) {
          queueOption.isSelected = false;
        }
      });
    }

    this.updateSelectedOptionText();
    this.onQueuesChange.emit([...this.selectedQueues]);
  }

  onQueueSelect(queueOption: QueueOption, event: Event): void {
    event.stopPropagation();

    queueOption.isSelected = !queueOption.isSelected;

    if (queueOption.isSelected) {
      if (!this.selectedQueues.includes(queueOption.name)) {
        this.selectedQueues.push(queueOption.name);
      }
    } else {
      const index = this.selectedQueues.indexOf(queueOption.name);
      if (index > -1) {
        this.selectedQueues.splice(index, 1);
      }
    }

    // Update parent group selection status
    const parentGroup = this.queueGroups.find(g => g.name === queueOption.parentGroup);
    if (parentGroup) {
      parentGroup.isSelected = this.isGroupSelected(parentGroup.name, parentGroup.queues);
    }

    this.updateSelectedOptionText();
    this.onQueuesChange.emit([...this.selectedQueues]);
  }

  onQueueCheckboxClick(queueOption: QueueOption, event: Event): void {
    event.stopPropagation();
    // Prevent the row click event from firing when clicking the checkbox
  }

  onQueueCheckboxChange(queueOption: QueueOption, event: Event): void {
    event.stopPropagation();

    // Toggle the queue selection
    queueOption.isSelected = !queueOption.isSelected;

    if (queueOption.isSelected) {
      if (!this.selectedQueues.includes(queueOption.name)) {
        this.selectedQueues.push(queueOption.name);
      }
    } else {
      const index = this.selectedQueues.indexOf(queueOption.name);
      if (index > -1) {
        this.selectedQueues.splice(index, 1);
      }
    }

    // Update parent group selection status
    const parentGroup = this.queueGroups.find(g => g.name === queueOption.parentGroup);
    if (parentGroup) {
      parentGroup.isSelected = this.isGroupSelected(parentGroup.name, parentGroup.queues);
    }

    this.updateSelectedOptionText();
    this.onQueuesChange.emit([...this.selectedQueues]);
  }

  updateSelectedOptionText(): void {
    const selectedCount = this.selectedQueues.length;
    if (selectedCount === 0) {
      this.selectedOptionText = 'Select queue names';
      this.isOptionSelected = false;
    } else if (selectedCount === 1) {
      this.selectedOptionText = this.selectedQueues[0];
      this.isOptionSelected = true;
    } else {
      this.selectedOptionText = `${selectedCount} options selected`;
      this.isOptionSelected = true;
    }
    this.cdr.markForCheck();
  }

  get filteredGroups() {
    const query = this.searchQuery.trim().toLowerCase();
    if (!query) return this.queueGroups;

    return this.queueGroups.filter(group =>
      group.name.toLowerCase().includes(query) ||
      group.queues.some(queue => queue.toLowerCase().includes(query))
    );
  }

  get filteredQueues() {
    const query = this.searchQuery.trim().toLowerCase();
    if (!query) return this.allQueues;

    return this.allQueues.filter(queue =>
      queue.name.toLowerCase().includes(query) ||
      queue.parentGroup.toLowerCase().includes(query)
    );
  }

  getQueueOption(queueName: string): QueueOption {
    return this.allQueues.find(q => q.name === queueName) || {
      name: queueName,
      isSelected: false,
      parentGroup: ''
    };
  }

  isQueueSelected(queueName: string): boolean {
    return this.selectedQueues.includes(queueName);
  }

  /** Queues in this group that match the current search query (or all queues when no search). */
  getFilteredQueuesForGroup(group: QueueGroup): string[] {
    const query = this.searchQuery.trim().toLowerCase();
    if (!query) return group.queues;
    return group.queues.filter(queue =>
      queue.toLowerCase().includes(query) || group.name.toLowerCase().includes(query)
    );
  }

  get filteredQueueCount(): number {
    const query = this.searchQuery.trim().toLowerCase();
    if (!query) return this.totalQueueCount;

    return this.filteredQueues.length;
  }

  get displayedQueueCount(): number {
    return this.filteredQueueCount;
  }

  get queueCountText(): string {
    const query = this.searchQuery.trim().toLowerCase();
    const count = !query ? this.totalQueueCount : this.filteredQueueCount;
    return count === 1 ? '1 option' : `${count} options`;
  }
}
